import { memo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut, Pie } from 'react-chartjs-2';
import styled from 'styled-components';
import { useAppSelector } from 'app/hooks';
import { selectPayroll } from 'features/payroll/payrollSlice';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const StyledWrapperChart = styled.div`
  width: 90%;
  height: 400px;
  margin: 0 auto;
  position: relative;
`;

const StyledChartContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledHeaderTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  font-family: Roboto;
  line-height: 20px;
  color: #121926;
  text-align: center;
  margin-top: 15px;
`;

const StyledWrapper = styled.div`
  margin-bottom: 8px;
`;

const COLORS_CHART = [
  { id: 1, value: '#FF5733' }, // Red
  { id: 2, value: '#33FF57' }, // Green
  { id: 3, value: '#3357FF' }, // Blue
  { id: 4, value: '#FF33A8' }, // Pink
  { id: 5, value: '#FF8C33' }, // Orange
  { id: 6, value: '#FFFF33' }, // Yellow
  { id: 7, value: '#33FFF6' }, // Cyan
  { id: 8, value: '#B833FF' }, // Purple
  { id: 9, value: '#33FFB5' }, // Mint
  { id: 10, value: '#FF3333' }, // Dark Red
  { id: 11, value: '#339FFF' }, // Light Blue
  { id: 12, value: '#A833FF' }, // Violet
  { id: 13, value: '#FFB533' }, // Light Orange
  { id: 14, value: '#33FF99' }, // Light Green
  { id: 15, value: '#FF33D1' }, // Magenta
];

function PayrollCostByDesignationChart() {
  const dataPayrollCostByDesignation = useAppSelector(selectPayroll)?.dataPayrollCostByDesignation;

  // Filter out the "Total" entry
  const filteredData = dataPayrollCostByDesignation?.filter(
    (item) => item?.designation?.toLowerCase() !== 'total',
  );

  const data = {
    labels: filteredData?.map((item) => item?.designation) || [],
    datasets: [
      {
        label: 'Salary Cost',
        data: filteredData?.map((item) => item?.totalSalaryCost) || [],
        backgroundColor: filteredData?.map((item, index) => COLORS_CHART[index]?.value) || [],
        borderWidth: 0, // Remove border
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
        align: 'center',
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: 12,
          font: {
            size: 12,
          },
          usePointStyle: false,
          padding: 16,
        },
        title: { display: false },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || '';
            const value = context.raw || 0;
            return `${label}: ${value.toLocaleString()}`;
          },
        },
      },
      datalabels: {
        formatter: (value: number) => {
          if (value >= 1000000000) {
            return (value / 1000000000).toFixed(1) + 'B';
          } else if (value >= 1000000) {
            return (value / 1000000).toFixed(1) + 'M';
          } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + 'K';
          }
          return value.toString();
        },
        color: '#000',
        font: {
          size: 12,
          weight: 'normal' as const,
        },
      },
    },
    layout: {
      padding: {
        left: 30,
        right: 30,
        top: 20,
        bottom: 20,
      },
    },
    cutout: '40%',
  };

  return (
    <StyledContainer>
      <StyledWrapperChart>
        <StyledWrapper>
          <StyledHeaderTitle>Payroll Cost by Designation</StyledHeaderTitle>
        </StyledWrapper>
        <StyledChartContainer>
          <Doughnut data={data} options={options} style={{ width: '100%', height: '100%' }} />
        </StyledChartContainer>
      </StyledWrapperChart>
    </StyledContainer>
  );
}

const StyledContainer = styled.div`
  padding: 16px 24px;
  background-color: var(--cl_white);
  border-radius: 12px;
  border: 0.5px solid var(--cl_gray200);
  height: 100%;
`;

export default memo(PayrollCostByDesignationChart);
